import { resolveApiBaseUrl } from "@/lib/apiClient"
import type { AppPlatform } from "./types"

const runtimeEnv = () =>
	(
		globalThis as typeof globalThis & {
			process?: { env?: Record<string, string> }
		}
	).process?.env ?? {}

const resolveZeroOrigin = () => {
	const env = runtimeEnv()

	if (env.BUN_PUBLIC_ZERO_ORIGIN) {
		return env.BUN_PUBLIC_ZERO_ORIGIN
	}

	if (typeof window !== "undefined") {
		const isLocalTarget =
			window.location.protocol === "file:" ||
			window.location.hostname === "localhost" ||
			window.location.hostname === "127.0.0.1" ||
			window.location.hostname === "::1"

		return isLocalTarget
			? "http://localhost:4848"
			: "https://zero.oblique.tools"
	}

	return env.NODE_ENV === "production"
		? "https://zero.oblique.tools"
		: "http://localhost:4848"
}

export const createBrowserPlatform = (): AppPlatform => {
	const apiOrigin = resolveApiBaseUrl()

	return {
		apiOrigin,
		kind: "web",
		name: "Oblique Web",
		zeroOrigin: resolveZeroOrigin(),
		getGoogleSignInUrl: (redirectPath) => {
			const search = new URLSearchParams()
			if (redirectPath?.startsWith("/")) {
				search.set("redirect", redirectPath)
			}
			const query = search.toString()
			return `${apiOrigin}/auth/google/start${query ? `?${query}` : ""}`
		},
		openExternal: async (url) => {
			window.location.href = url
		},
		getReactNodeBundle: async (projectID, nodeID, shareToken) => {
			const path = shareToken
				? `/react-bundles/shares/${encodeURIComponent(shareToken)}/nodes/${encodeURIComponent(nodeID)}`
				: `/react-bundles/projects/${encodeURIComponent(projectID)}/nodes/${encodeURIComponent(nodeID)}`
			const response = await fetch(`${apiOrigin}${path}`, {
				credentials: "include",
			})
			const payload = (await response.json().catch(() => null)) as {
				hash?: unknown
				javascript?: unknown
				message?: unknown
			} | null
			if (
				!response.ok ||
				!payload ||
				typeof payload.hash !== "string" ||
				typeof payload.javascript !== "string"
			) {
				throw new Error(
					typeof payload?.message === "string"
						? payload.message
						: "React node bundle is unavailable",
				)
			}
			return { hash: payload.hash, javascript: payload.javascript }
		},
	}
}
